
import React, { useState, useEffect, useRef, createRef, useContext, forwardRef } from "react";
import SidePanel from "../../components/side-panel/SidePanel";
import AppBar from "../../components/side-panel/AppBar";
import BasemapToggleWidget from "../../widgets/esri/basemap-toggle/BasemapToggleWidget";
import Home from "../../widgets/esri/home/HomeWidget";
import NavMenu from "../../components/side-panel/NavMenu";
import BugReportWidget from "../../widgets/custom/bug-report/BugReportWidget";
import CoordinateConversionWidget from "../../widgets/esri/coordinate-conversion/CoordinateConversionWidget";
import SearchWidget from "../../widgets/esri/search/SearchWidget";
import DocViewerWidget from "../../widgets/custom/doc-viewer/DocViewerWidget";
import ExtentChangeWidget from "../../widgets/custom/prevnext-extent/ExtentChangeWidget";
import GoogleViewWidget from "../../widgets/custom/google-view/GoogleViewWidget"
import { defaultRightWindowWidget } from "../PageConfiguration"
import SwipeWidget from "../../widgets/esri/swipe/SwipeWidget";
import FullScreenMapWidget from "../../widgets/esri/full-screen-map/FullScreenMapWidget"
import CompassWidget from "../../widgets/esri/compass/CompassWidget";
import ScaleBarWidget from "../../widgets/esri/scaleBar/ScaleBarWidget";
import AimWidget from "../../widgets/custom/aim/AimWidget"
import BasemapOpacityWidget from "../../widgets/custom/basemap-opacity/BasemapOpacityWidget";
import { useFullScreen } from 'react-hooks-full-screen';
import HorizPanel from "../../components/horiz-panel/HorizPanel";
import EmbedMapContainer from "../../widgets/custom/embed-map-container/EmbedMapContainer";
import { OCLandInsightsContext } from "../../hooks/context/OcLandInsightsContext";
import { PageConfigurationContext } from "../../hooks/context/PageConfigurationContext";
import { ShowMapContext } from "../../hooks/context/ShowMapContext";

const SurveyMap = forwardRef<HTMLDivElement,any>(({data},ref) => {
  const {portal,view,viewReady}=data

  const pageConfigurationCtx: any = useContext(PageConfigurationContext);
  const ocLandInsightsDictionaryCtx: any = useContext(OCLandInsightsContext);
  const showMapCtx:any=useContext(ShowMapContext);

  const [showFullScreen, setShowFullScreen] = useState(false)
  useFullScreen('showFullScreen', showFullScreen)

  const [page, setPage] = useState<string>("layers");
  const [tool, setTool] = useState<string>("");
  const [hideDrawer, setHideDrawer] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [mapSplit, setMapSplit] = useState<number>(1);
  const [leftWidth, setLeftWidth] = useState<any>();
  const [separatorXPosition, setSeparatorXPosition] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [bar, setBar] = useState<undefined|string>();
  const [topHeight, setTopHeight] = useState<any>(0);
  const [separatorYPosition, setSeparatorYPosition] = useState(0);
  const [splitPaneHeightRef, setSplitPaneHeightRef] = useState<any>();
  const [topRef, setTopRef] = useState<any>();
  const [tableSplit, setTableSplit] = useState<number>(1)
  const [rightWindow, setRightWindow] = useState<string>(defaultRightWindowWidget)
  const leftRef = createRef<HTMLDivElement>();
  const resizeInProgress = useRef(false);

  useEffect(() => {

    const handleResize = () => {
      if (resizeInProgress.current === true) {
        return;
      }
      resizeInProgress.current = true;
      throttled_updateWindowSize();
    }
    const throttled_updateWindowSize = () => {
      setTimeout(() => {
        setWindowWidth(window.innerWidth);
        resizeInProgress.current = false;
      }, 1000);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, bar: string) => {
    setBar(bar);
    setSeparatorYPosition(e.clientY);
    setSeparatorXPosition(e.clientX);
    setDragging(true);
  };
  //Making sure the maps don't get too small. Theres is also a min-width in the css as a backup
  const MIN_WIDTH = 150;
  const MIN_HEIGHT = 150;

  const onMouseMove = (e: { clientX: string | React.SetStateAction<number>; clientY: React.SetStateAction<number>; }) => {
    if (dragging && bar === "horz") {
      const newLeftWidth = leftWidth + e.clientX - separatorXPosition; //Current width of left panel + mouse position - current handle bar position
      setSeparatorXPosition(Number(e.clientX));

      if (newLeftWidth < MIN_WIDTH) {
        setLeftWidth(MIN_WIDTH);
        return;
      }

      if (topRef && topRef.current) {
        const splitPaneWidth = topRef.current.clientWidth;

        if (newLeftWidth > splitPaneWidth - MIN_WIDTH) {
          setLeftWidth(splitPaneWidth - MIN_WIDTH);
          return;
        }
      }
      setLeftWidth(newLeftWidth);
    } else if (dragging && bar === "vert") {
      const newTopHeight = topHeight + e.clientY - separatorYPosition;
      setSeparatorYPosition(e.clientY);

      if (splitPaneHeightRef) {
        const splitPaneHeight = splitPaneHeightRef.clientHeight;
        if (newTopHeight > splitPaneHeight - MIN_HEIGHT) {
          setTopHeight(splitPaneHeight - MIN_HEIGHT);
          return;
        }
      }
      setTopHeight(newTopHeight);
    }
  };

  const onMouseUp = () => {
    setDragging(false);
    setBar("");
  };

  useEffect(() => {
    // if(!rightWindow.length){
    //   return
    // }
    if (leftRef.current) {
      if (!leftWidth) {
        setLeftWidth(leftRef.current?.clientWidth);
      }
      if (mapSplit === 1){
        leftRef.current.style.width = "100%";
      } else if(mapSplit === 3 && rightWindow.length) {
        leftRef.current.style.width = "100%";
      } else if(mapSplit===2 && rightWindow.length) {
        leftRef.current.style.width = `${leftWidth}px`;
      }
    }
  }, [leftRef, leftWidth, setLeftWidth,rightWindow]);

  useEffect(() => {

    if ((mapSplit === 1 || (mapSplit === 3 && rightWindow.length)) && leftRef.current) {
      leftRef.current.style.width = '100%'
    } else if (mapSplit === 2 && leftRef.current && rightWindow.length) {
      setLeftWidth('calc(50% - 1rem)')
      leftRef.current.style.width = 'calc(50% - 1rem)'
    }

  }, [mapSplit,rightWindow])

  useEffect(()=>{
    if(!rightWindow){
      setMapSplit(1)
    }

  },[rightWindow])

  useEffect(() => {
    if (topRef) {
      if (!topHeight) {
        setTopHeight(topRef?.clientHeight);
      }
      if (tableSplit === 1 || tableSplit === 3) {
        topRef.style.height = "100%";
      } else {
        topRef.style.height = `${topHeight}px`;
      }
    }
  }, [topRef, topHeight, setTopHeight, tableSplit]);

  useEffect(() => {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  });

    return (
      <>
        {windowWidth <= 900 ? (
          <div className="size-error">
            <h1 className="size-error__title">
              Please open this app on a larger screen
            </h1>
          </div>
        ) : null}
        <div style={showMapCtx.data?{display:'block'}:{display:'none'}}>
        <div className="root">
          <div className="drawercont">
            <div className="drawercont__navcont">
              <NavMenu
                setPage={setPage}
                page={page}
                setTool={setTool}
                hideDrawer={hideDrawer}
                setHideDrawer={setHideDrawer}
                setOpen={setIsOpen}
                pageConfig={pageConfigurationCtx.data}
              />
            </div>
            {viewReady&&<SidePanel
            portal={portal} 
              view={view} hideDrawer={hideDrawer} 
              setHideDrawer={setHideDrawer} setPage={setPage} 
              page={page} tool={tool} pageConfig={pageConfigurationCtx.data} mapId={pageConfigurationCtx.data?.mapId} 
              setHorizPanelHeight={setTopHeight} 
              tableSplit={tableSplit} setTableSplit={setTableSplit} />}
          </div>
          <div className="mapcont"style={{overflow:'hidden'}}>
            {viewReady&&<AppBar setMapSplit={setMapSplit} portal={portal} pageConfig={pageConfigurationCtx.data} mapSplit={mapSplit} tableSplit={tableSplit} setTableSplit={setTableSplit} rightWindow={rightWindow}
              setRightWindow={setRightWindow} />}
            <div className="mapcont__view">
              <div
                ref={(e) => setSplitPaneHeightRef(e)}
                className="mapcont__view__cont"
              >
                <div className="splitpane" ref={(e) => setTopRef(e)}>
                  <div
                    className="splitpane__map splitpane__map--left"
                    ref={leftRef}
                    style={
                      mapSplit === 3 && rightWindow.length ? { display: "none" } : { display: "block" }
                    }
                  >
                  <div id="mapDiv" className="mapDiv" ref={ref}></div>

                    {viewReady&&
                    <>
                    
                    <Home view={view} />
                    <>{pageConfigurationCtx.data?.widgets.swipe && <SwipeWidget view={view} activeWidget={"swipeWidget"}></SwipeWidget>}</>
                    <CompassWidget view={view} />
                    <ExtentChangeWidget view={view} />
                    <SearchWidget view={view} />
                    <FullScreenMapWidget view={view} />
                    <ScaleBarWidget view={view} />
                    <BasemapOpacityWidget view={view} mapId={pageConfigurationCtx.data?.mapId} />
                    {pageConfigurationCtx.data?.widgets?.basemapToggle?.show && <BasemapToggleWidget view={view} mapId={pageConfigurationCtx.data?.widgets?.basemapToggle?.mapId} />}
                    {pageConfigurationCtx.data?.widgets.coordinateConversion && <CoordinateConversionWidget view={view} />}
                    </>
                    }
                  </div>
                  <div className="splitpane__divider splitpane__divider--ver" onMouseDown={e => onMouseDown(e, 'horz')} style={mapSplit !==1 && rightWindow.length ? { display: 'block' } : { display: 'none' }}>
                    <div className="splitpane__divider__handle splitpane__divider__handle--ver">
                      <span className="splitpane__divider__handle__bar splitpane__divider__handle__bar--ver"></span>
                      <span className="splitpane__divider__handle__bar splitpane__divider__handle__bar--ver"></span>
                    </div>
                  </div>
                  <div
                    className="splitpane__map splitpane__map--right"
                    style={
                      mapSplit !== 1 && rightWindow.length ?{ display: "block" } : { display: "none"}
                    }
                  >{viewReady&&
                    <>
                    {/* <AimWidget activeWidget={rightWindow}></AimWidget> */}
                    {pageConfigurationCtx.data?.widgets?.streetView && <GoogleViewWidget view={view} activeWidget={rightWindow} mapSplit={mapSplit} setMapSplit={setMapSplit}></GoogleViewWidget>}
                    <DocViewerWidget dragging={dragging} view={view} activeWidget={rightWindow} mapSplit={mapSplit} setMapSplit={setMapSplit}></DocViewerWidget>
                    </>
                  }
                  </div>
                </div>

                <div
                  className="splitpane__divider splitpane__divider--hor"
                  onMouseDown={(e) => onMouseDown(e, "vert")}
                  style={{ backgroundColor: "#14976b", color: "white" }}
                >

                  <div>
                    {tableSplit === 2 ? (
                      <div onClick={() => setTableSplit(1)} className="splitpane__divider__handle splitpane__divider__handle--hor">
                        <span style={{ cursor: "pointer", marginBottom: "10px" }}>▼</span>
                      </div>
                    ) : (
                      <div onClick={() => setTableSplit(2)} className="splitpane__divider__handle splitpane__divider__handle--hor">
                        <span style={{ cursor: "pointer", marginBottom: "10px" }}>▲</span>
                      </div>
                    )}
                  </div>
                </div>
                <div id="showFullScreen"
                  className={tableSplit === 2 ? 'survey' : 'survey__hidden'}
                >
                  {/* beginning of table panel */}
                  {viewReady&&
                  <HorizPanel view={view} setShowFullScreen={setShowFullScreen} showFullScreen={showFullScreen} pageConfig={pageConfigurationCtx.data} layerIds={ocLandInsightsDictionaryCtx.data}></HorizPanel>
                    }
                  {/* end of second part */}
                </div>
              </div>
            </div>
          </div>
          {pageConfigurationCtx.data?.widgets.bugReport && <BugReportWidget open={isOpen} setOpen={setIsOpen} />}
        </div>
        </div>
      </>
    )

});

export default SurveyMap;
